'use client';

import * as Icons from 'lucide-react';
import { applyTextStyle } from '@/lib/textStyle';

/**
 * StatsBlock — bandeau de chiffres-cles.
 * Backgrounds : gradient | primary | white | custom (couleur hex).
 *
 * Si config.bg_color est defini (hex), il prend la priorité sur bg_style.
 * Permet a l'admin d'avoir une couleur exacte de la charte du client sans
 * passer par les presets de thème.
 */
export default function StatsBlock({ config }) {
  const items = config.items || [];
  const bg = config.bg_style || 'gradient';
  const customBg = config.bg_color || ''; // hex like "#991414"
  if (items.length === 0) return null;

  const backgrounds = {
    gradient: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
    primary: 'var(--color-primary)',
    white: '#fff',
  };
  const colors = {
    gradient: { text: 'var(--color-primary-text)', muted: 'rgba(255,255,255,0.92)' },
    primary: { text: 'var(--color-primary-text)', muted: 'rgba(255,255,255,0.92)' },
    white: { text: 'var(--color-text)', muted: 'var(--color-text-muted)' },
  };

  // Si custom hex defini, on calcule le bg + couleurs de texte automatiquement
  // selon la luminosite (texte blanc sur fond sombre, texte sombre sur fond clair).
  const isCustom = !!customBg;
  const finalBg = isCustom ? customBg : backgrounds[bg];
  const c = isCustom
    ? (isHexDark(customBg)
        ? { text: '#ffffff', muted: 'rgba(255,255,255,0.92)' }
        : { text: '#0f172a', muted: 'rgba(15,23,42,0.7)' })
    : colors[bg];

  return (
    <section style={{
      padding: '36px 0',
      background: finalBg,
      color: c.text,
      ...(!isCustom && bg === 'white' ? { borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)' } : {}),
    }}>
      <div className="container-page">
        {config.title && (
          <h2 style={applyTextStyle(config.title_style, {
            fontSize: 'clamp(22px, 3vw, 32px)',
            fontWeight: 800, textAlign: 'center',
            margin: '0 0 40px',
            letterSpacing: '-0.02em', color: c.text,
          })}>{config.title}</h2>
        )}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(160px, 1fr))`,
          gap: '32px',
          textAlign: 'center',
        }}>
          {items.map((s, i) => (
            <StatCard key={i} stat={s} bg={bg} colors={c} />
          ))}
        </div>
      </div>
    </section>
  );
}

// Detecte si une couleur hex est "sombre" (luminance perçue < 0.5).
function isHexDark(hex) {
  if (!hex || typeof hex !== 'string') return false;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}

function StatCard({ stat, bg, colors }) {
  const Icon = (stat.icon && Icons[stat.icon]) || Icons.Award;
  // Pour le iconBg/iconColor : si on est sur un fond clair (white) ou si la
  // couleur computee suggere texte sombre → background icon en orange tinted +
  // icon orange. Sinon (fond sombre) → background icon blanc transparent +
  // icon blanc.
  const isLightBg = bg === 'white' || (colors.text === '#0f172a');
  const iconBg = isLightBg ? 'rgba(226, 114, 40, 0.1)' : 'rgba(255,255,255,0.18)';
  const iconColor = isLightBg ? 'var(--color-primary)' : '#fff';

  return (
    <div>
      <div style={{
        width: '52px', height: '52px',
        borderRadius: '14px', background: iconBg, color: iconColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 16px',
      }}>
        <Icon size={24} strokeWidth={2.4} />
      </div>
      <div style={applyTextStyle(stat.value_style, {
        fontSize: 'clamp(32px, 5vw, 48px)',
        fontWeight: 800, lineHeight: 1.1,
        letterSpacing: '-0.03em', marginBottom: '6px',
        color: colors.text,
      })}>{stat.value || '—'}</div>
      <div style={applyTextStyle(stat.label_style, {
        fontSize: '14px', fontWeight: 500,
        color: colors.muted,
      })}>{stat.label || ''}</div>
    </div>
  );
}
